import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip.tsx';
import {
  FormProvider,
  type FieldPath,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FormPhoneField } from '~/components/FormComponents/FormPhoneField.tsx';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type { TGuaranteesPrincipal } from '~/store/guarantees/types.ts';

import {
  PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS,
  PRINCIPAL_REQUIRED_FIELDS,
} from '../../../rootSection/constants.ts';
import {
  GuaranteesPrincipalFieldName,
  type IPrincipalSectionProps,
} from '../../../rootSection/types.ts';
import {
  buildGuaranteesPrincipalPayload,
  getGuaranteesPrincipalInitialValues,
  guaranteesPrincipalSectionResolver,
} from '../../../utils/sections/utils.ts';
import FlowSection from '../shared/FlowSection.tsx';
import { useFlowSectionFormState } from '../shared/useFlowSectionFormState.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';

const PrincipalSection: React.FC<IPrincipalSectionProps> = ({
  principal,
  isLoading,
  isSaving,
  onSubmit,
}) => {
  const {
    isReadOnly,
    requireChangesToEnableActions,
    onDependentSectionDirtyChange,
  } = useGuaranteesRootApplicationSectionContext();
  const defaultValues = React.useMemo(
    () => getGuaranteesPrincipalInitialValues(principal),
    [principal]
  );
  const formMethods = useForm<TGuaranteesPrincipal>({
    defaultValues,
    resolver: guaranteesPrincipalSectionResolver,
    mode: 'onSubmit',
  });
  const {
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = formMethods;
  const isDisabled = isSaving || isReadOnly;
  const handleDirtyChange = React.useCallback(
    (isDirty: boolean) => onDependentSectionDirtyChange('principal', isDirty),
    [onDependentSectionDirtyChange]
  );
  const { isActionDisabled } = useFlowSectionFormState({
    defaultValues,
    isDirty,
    isDisabled,
    onDirtyChange: handleDirtyChange,
    requireChangesToEnableActions,
    reset,
  });

  const handlePrincipalSubmit: SubmitHandler<TGuaranteesPrincipal> = async (values) => {
    const isSaved = await onSubmit(buildGuaranteesPrincipalPayload(values));

    if (isSaved) {
      reset(values);
    }
  };

  const touchAndValidateFields = React.useCallback(
    (fieldNames: readonly (keyof TGuaranteesPrincipal)[]) => {
      fieldNames.forEach((fieldName) => {
        const fieldPath = fieldName as FieldPath<TGuaranteesPrincipal>;

        setValue(fieldPath, getValues(fieldPath), {
          shouldDirty: false,
          shouldTouch: true,
          shouldValidate: false,
        });
      });

      void trigger(fieldNames as FieldPath<TGuaranteesPrincipal>[]);
    },
    [getValues, setValue, trigger]
  );

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      touchAndValidateFields(PRINCIPAL_REQUIRED_FIELDS);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [defaultValues, touchAndValidateFields]);

  return (
    <FlowSection
      step={guaranteesClientText.principalSectionStepLabel}
      title={guaranteesClientText.principalSectionTitle}
      description={guaranteesClientText.principalSectionDescription}
    >
      {isLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {guaranteesClientText.principalLoadingText}
          </Typography>
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(handlePrincipalSubmit)(event);
            }}
          >
            <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalIdentifyCode}
                label={guaranteesClientText.principalIdentifyCodeLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalFullName}
                label={guaranteesClientText.principalFullNameLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalShortName}
                label={guaranteesClientText.principalShortNameLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalLocation}
                label={guaranteesClientText.principalLocationLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalNameAddressGuaranteeLang}
                label={
                  <Box component="span" sx={guaranteesClientPageStyles.flowInlineTooltipLabel}>
                    {guaranteesClientText.principalNameAddressGuaranteeLangLabel}
                    <InfoTooltip
                      title={guaranteesClientText.principalNameAddressGuaranteeLangTooltip}
                      iconStyles={guaranteesClientPageStyles.flowInlineTooltipIcon}
                    />
                  </Box>
                }
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalContactFullName}
                label={guaranteesClientText.principalContactFullNameLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalContactEmail}
                label={guaranteesClientText.principalContactEmailLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <Box sx={guaranteesClientPageStyles.flowPhoneField}>
                <FormPhoneField
                  name={GuaranteesPrincipalFieldName.PrincipalContactPhone}
                  label={guaranteesClientText.principalContactPhoneLabel}
                  disabled={isDisabled}
                />
              </Box>
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalAuthorizedPersonPosition}
                label={guaranteesClientText.principalAuthorizedPersonPositionLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormInput
                name={GuaranteesPrincipalFieldName.PrincipalAuthorizedPersonName}
                label={guaranteesClientText.principalAuthorizedPersonNameLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormAutocomplete
                name={GuaranteesPrincipalFieldName.PrincipalHasChangesInConstituentDocuments}
                label={guaranteesClientText.principalHasChangesInConstituentDocumentsLabel}
                options={PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS}
                disabled={isDisabled}
                notNullable
                sx={guaranteesClientPageStyles.flowField}
              />
            </Box>

            <Box sx={guaranteesClientPageStyles.flowActions}>
              <Button
                type="submit"
                variant="contained"
                disabled={isActionDisabled}
                startIcon={isSaving ? <ButtonLoadingIcon /> : undefined}
                sx={dataFormStyles.documentPrimaryAction}
              >
                {guaranteesClientText.savePrincipalButton}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      )}
    </FlowSection>
  );
};

export default PrincipalSection;
